import { mapName } from '@gardener/content';
import { MAP_SHOTS, type MapShotMessage, type MapTiles, type MapTilesIndexView } from '@gardener/protocol';
import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import { ApiError, call, put } from './api';
import { Icon, IconButton } from './ui';

/**
 * The game's client (ADR-0024): garden-wars.io beside admin.garden-wars.io; in dev the client's Vite on :5173
 * (`VITE_GAME_URL` for another).
 */
export const GAME = (
  (import.meta.env.VITE_GAME_URL as string | undefined) ??
  (location.hostname.startsWith('admin.')
    ? `${location.protocol}//${location.hostname.slice('admin.'.length)}`
    : `${location.protocol}//${location.hostname}:5173`)
).replace(/\/+$/, '');
const GAME_ORIGIN = new URL(GAME).origin;
/** The game's window in the iframe (a 1080p player's, in CSS px), and how wide its preview is. */
const [FRAME_W, FRAME_H, PREVIEW_W] = [1920, 1080, 352];
/** Tiles uploaded at a time. */
const UPLOADS = 8;

type Step =
  | { phase: 'starting' }
  | { phase: 'saving' }
  | { phase: 'frames' | 'tiles' | 'upload'; done: number; of: number }
  | { phase: 'failed'; error: string };

/** A call tried again twice, a little later each time: the network, a server restarting, a rate limit's minute. */
async function retry(f: () => Promise<void>): Promise<void> {
  for (let i = 0; ; i++) {
    try {
      return await f();
    } catch (err) {
      const again = !(err instanceof ApiError) || err.status >= 500 || err.status === 429;
      if (i >= 2 || !again) throw err;
      await new Promise((done) => setTimeout(done, 1500 * (i + 1)));
    }
  }
}

/**
 * A map shot again (ADR-0223 amended: Shoot): the game's own client in an iframe (`?mapshot`, `MAP_SHOTS`), seen live in
 * the map's corner, sweeps the map on this browser's GPU and hands back its tiles and manifest (the client's
 * `app/mapshot.ts`); those the map's newest shot (`known`) lacks go up one by one, then the manifest. The shot is of the
 * version the server's build has (`now`): the client says which it draws, and one that differs (a deploy half done) is
 * refused before it starts.
 */
export function MapShoot(props: {
  map: string;
  now: string;
  known: MapTiles | null;
  done: (index: MapTilesIndexView, fresh: number, all: number) => void;
  close: () => void;
}) {
  let frame!: HTMLIFrameElement;
  const [step, setStep] = createSignal<Step>({ phase: 'starting' });
  let gone = false;
  onCleanup(() => {
    gone = true;
  });
  const fail = (error: string) => setStep({ phase: 'failed', error });

  async function send(manifest: MapTiles, files: [string, Blob][]) {
    const known = new Set(Object.values(props.known?.tiles ?? {}));
    const fresh = files.filter(([sha]) => !known.has(sha));
    let done = 0;
    setStep({ phase: 'upload', done, of: fresh.length });
    const queue = fresh.values();
    const upload = async () => {
      for (let next = queue.next(); !next.done && !gone; next = queue.next()) {
        const [sha, webp] = next.value;
        await retry(() => put(`/api/admin/map-tile/${sha}`, webp));
        setStep({ phase: 'upload', done: ++done, of: fresh.length });
      }
    };
    await Promise.all(Array.from({ length: UPLOADS }, upload));
    if (gone) return;
    setStep({ phase: 'saving' });
    const path = `/api/admin/map-tiles/${manifest.map}/${manifest.version}`;
    props.done(await call<MapTilesIndexView>('PUT', path, manifest), fresh.length, files.length);
  }

  onMount(() => {
    const hear = (e: MessageEvent<MapShotMessage<Blob>>) => {
      if (e.origin !== GAME_ORIGIN || e.source !== frame.contentWindow) return;
      const m = e.data;
      if (m.mapshot === 'ready') {
        if (m.map !== props.map) return fail(`The game opened ${mapName(m.map)}, not ${mapName(props.map)}.`);
        if (m.version !== props.now)
          return fail(
            `The game at ${new URL(GAME).host} draws ${m.version.slice(0, 7)}, the server’s build has ${props.now.slice(0, 7)}: shoot once both are deployed.`,
          );
        frame.contentWindow?.postMessage({ mapshot: 'shoot' } satisfies MapShotMessage, GAME_ORIGIN);
      } else if (m.mapshot === 'frames' || m.mapshot === 'tiles') setStep({ phase: m.mapshot, done: m.done, of: m.of });
      else if (m.mapshot === 'shot') send(m.manifest, m.files).catch((err: unknown) => fail((err as Error).message));
      else if (m.mapshot === 'failed') fail(m.error);
    };
    addEventListener('message', hear);
    onCleanup(() => removeEventListener('message', hear));
  });

  const line = () => {
    const s = step();
    if (s.phase === 'failed') return s.error;
    if (s.phase === 'starting') return 'Starting the game';
    if (s.phase === 'saving') return 'Saving the shot';
    if (s.phase === 'frames') return `Shooting frame ${s.done} of ${s.of}`;
    if (s.phase === 'tiles') return `Making tiles · ${s.done} of ${s.of}`;
    return s.of ? `Uploading new tiles · ${s.done} of ${s.of}` : 'No new tiles';
  };
  /** The step's share done; null: under way, no count yet. */
  const share = () => {
    const s = step();
    return 'of' in s ? (s.of ? s.done / s.of : 1) : null;
  };
  const failed = () => step().phase === 'failed';
  return (
    <div
      class="bg-surface-elevated-bg border-surface-elevated-border absolute bottom-4 left-4 z-10 flex flex-col overflow-hidden rounded-xl border shadow-lg"
      style={{ width: `${PREVIEW_W}px` }}
    >
      <div
        class="relative overflow-hidden bg-black"
        style={{ width: `${PREVIEW_W}px`, height: `${(PREVIEW_W * FRAME_H) / FRAME_W}px` }}
      >
        <iframe
          ref={frame}
          src={`${GAME}/?autostart&mute&${MAP_SHOTS[props.map]}`}
          title={`${mapName(props.map)} in the game`}
          tabIndex={-1}
          class="pointer-events-none absolute top-0 left-0 origin-top-left border-0"
          style={{ width: `${FRAME_W}px`, height: `${FRAME_H}px`, transform: `scale(${PREVIEW_W / FRAME_W})` }}
        />
      </div>
      <div class="flex flex-col gap-2 p-3" role="status">
        <div class="flex items-center gap-2">
          <Icon name="camera" class="text-fg-secondary size-4 shrink-0" />
          <span class="min-w-0 flex-1 truncate text-[13px] font-medium">
            Shooting {mapName(props.map)}{' '}
            <span class="text-fg-muted font-mono font-normal">{props.now.slice(0, 7)}</span>
          </span>
          <IconButton label={failed() ? 'Close' : 'Cancel'} onClick={props.close}>
            <Icon name="x" class="size-4" />
          </IconButton>
        </div>
        <span class="text-fg-secondary flex items-start gap-1.5 text-xs text-pretty">
          <Show when={failed()}>
            <Icon name="danger" class="size-3.5 shrink-0" />
          </Show>
          {line()}
        </span>
        <Show when={!failed()}>
          <div class="bg-surface-100-bg h-1.5 overflow-hidden rounded-full">
            <div
              class={`bg-brand h-full rounded-full transition-[width] duration-300 ${share() === null ? 'w-1/4 animate-pulse' : ''}`}
              style={share() === null ? {} : { width: `${Math.round((share() ?? 0) * 100)}%` }}
            />
          </div>
          <span class="text-fg-muted text-xs">Keep this tab in front: a hidden tab draws nothing.</span>
        </Show>
      </div>
    </div>
  );
}
